/**
 * Beneficial Owner Letters — Appendix A/B/C (M32109) mapper.
 */
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';
import {
  emptyBoAppendixSurvey,
  type BoAppendixSurvey,
} from '../components/boAppendix/boAppendixSurvey';
import {
  emptyBoAppendixCriteria,
  type BoAppendixCriteriaState,
} from '../components/boAppendix/mapBoJsonToAppendix';
import type { BoAppendixDocCode } from '../components/boAppendix/boAppendixFields';
import { composeMyAddressLine } from '@/shared/utils/malaysianAddress';

export type BoAppendixCriteriaByDoc = Partial<Record<BoAppendixDocCode, BoAppendixCriteriaState>>;

export type BoAppendixAddressParts = {
  street01: string;
  street02?: string;
  street03?: string;
  city: string;
  state: string;
  post_code: string;
  country: string;
};

export interface BoAppendixState {
  values: Record<string, string>;
  signers: Record<string, SignerEntry[]>;
  survey: BoAppendixSurvey;
  /** @deprecated 단일 criteria — 복원 호환용. 실제는 criteriaByDoc 사용 */
  criteria?: BoAppendixCriteriaState;
  criteriaByDoc: BoAppendixCriteriaByDoc;
  /** 주소 필드키 → 구조화 주소 (Register 시 L_CONTACT_ADDRESS 용) */
  addresses: Record<string, BoAppendixAddressParts>;
}

export function emptyBoAppendixState(): BoAppendixState {
  return {
    values: {},
    signers: {},
    survey: emptyBoAppendixSurvey(),
    criteriaByDoc: {
      BA01: emptyBoAppendixCriteria(),
      BA02: emptyBoAppendixCriteria(),
      BA03: emptyBoAppendixCriteria(),
    },
    addresses: {},
  };
}

export function composeBoAppendixAddressLine(a: Partial<BoAppendixAddressParts> | null | undefined): string {
  if (!a) return '';
  return composeMyAddressLine({
    line: a.street01,
    postcode: a.post_code,
    town: a.city,
    state: a.state,
    country: a.country,
  });
}

export function toBoAppendixAddressParts(a: {
  line?: string;
  town?: string;
  state?: string;
  postcode?: string;
  country?: string;
  street?: string;
  street01?: string;
  city?: string;
  post_code?: string;
}): BoAppendixAddressParts {
  return {
    street01: String(a.street01 || a.street || a.line || '').trim(),
    street02: '',
    street03: '',
    city: String(a.city || a.town || '').trim(),
    state: String(a.state || '').trim(),
    post_code: String(a.post_code || a.postcode || '').trim(),
    country: String(a.country || '').trim(),
  };
}

export function ensureCriteriaDoc(
  state: BoAppendixState,
  docCode: BoAppendixDocCode,
): BoAppendixCriteriaState {
  if (!state.criteriaByDoc) state.criteriaByDoc = {};
  if (!state.criteriaByDoc[docCode]) {
    state.criteriaByDoc[docCode] = emptyBoAppendixCriteria();
  }
  return state.criteriaByDoc[docCode]!;
}

export function buildBoAppendixVariables(state: BoAppendixState): Record<string, any> {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(state.values || {})) {
    out[k] = v ?? '';
  }
  // BA01 criteria → 최상위 (레거시/단일 문서 호환) — Word [[cat_*]]/[[crit_*]] 는 ☒·☐
  const c1 = state.criteriaByDoc?.BA01 || state.criteria || emptyBoAppendixCriteria();
  Object.assign(out, criteriaFlagsForDoc(c1));
  return out;
}

/** 문서별 criteria → formData (Word [[cat_*]]/[[crit_*]]=☒·☐, [[pct_*]]=숫자) */
export function criteriaFlagsForDoc(c: BoAppendixCriteriaState | null | undefined): Record<string, string> {
  const empty = emptyBoAppendixCriteria();
  const cat = c?.cat || empty.cat;
  const crit = c?.crit || empty.crit;
  const pct = c?.pct || empty.pct;
  const box = (on: boolean) => (on ? '☒' : '☐');
  const out: Record<string, string> = {
    cat_direct: box(!!cat.direct),
    cat_indirect: box(!!cat.indirect),
    cat_both: box(!!cat.both),
    cat_control: box(!!cat.control),
  };
  for (const [k, v] of Object.entries(crit)) {
    out[`crit_${k}`] = box(!!v);
  }
  // 빈 값이어도 치환해 점선(……)이 남지 않도록 항상 키 출력
  out.pct_direct_shares = pct.direct_shares || '';
  out.pct_direct_voting = pct.direct_voting || '';
  out.pct_indirect_shares = pct.indirect_shares || '';
  out.pct_indirect_voting = pct.indirect_voting || '';
  out.pct_combined_shares = pct.combined_shares || '';
  out.pct_combined_voting = pct.combined_voting || '';
  return out;
}

/**
 * Appendix C Q13 — SMReasonId(1|2|3) → Word 체크 글리프.
 *   1 no BO / 2 cannot identify / 3 in process
 */
export function smReasonFlagsFromId(raw: unknown): Record<string, string> {
  const n = Number(String(raw ?? '').trim());
  const box = (on: boolean) => (on ? '☒' : '☐');
  return {
    sm_reason_no_bo: box(n === 1),
    sm_reason_cannot_identify: box(n === 2),
    sm_reason_in_process: box(n === 3),
    sm_reason_id: (n === 1 || n === 2 || n === 3) ? String(n) : '',
  };
}
